import { useCallback } from 'react';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import {
  useConfirmationConfigQuery,
  useSetConfirmationModeMutation,
} from '../store/api';

import { type ConfirmationMode, ConfirmationModeView } from './ConfirmationModeView';

/** Container: resolves the org-wide confirmation mode (falling back to the
 * server-declared default when unconfigured) and upserts on selection.
 * Org-wide only in this UI — per-encounter-type overrides exist in the API
 * as a fast-follow. */
export function ConfirmationModeSection() {
  const { data, isLoading } = useConfirmationConfigQuery();
  const [setMode, { isLoading: isSaving }] = useSetConfirmationModeMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { schedulingSettings, error: errorTracking } = useEventTracking();

  const orgWide = data?.items.find(item => !item.encounter_type_id);
  const mode = (orgWide?.mode ??
    data?.default_mode ??
    'pending_ack') as ConfirmationMode;

  const handleModeChange = useCallback(
    (nextMode: ConfirmationMode) => {
      schedulingSettings.confirmationModeChanged({ mode: nextMode });
      void (async () => {
        try {
          await setMode({
            setConfirmationRequest: { mode: nextMode },
          }).unwrap();
        } catch {
          enqueueSnackbar('Could not update the confirmation mode', {
            variant: 'error',
          });
          errorTracking.track(
            'Could not update the confirmation mode',
            'scheduling_confirmation_mode_update',
          );
        }
      })();
    },
    [setMode, schedulingSettings, enqueueSnackbar, errorTracking],
  );

  return (
    <ConfirmationModeView
      mode={mode}
      isLoading={isLoading}
      disabled={isSaving}
      onModeChange={handleModeChange}
    />
  );
}
